import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Phone, Menu, X, MessageCircle } from 'lucide-react';
import { Button } from './ui/button';
import { clinicData } from '../data';

const Header = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleWhatsAppCall = () => {
    window.open(`https://wa.me/91${clinicData.contact.whatsappNumber}?text=Hello, I would like to book an appointment`, '_blank');
  };

  const navLinks = [
    { path: '/', label: 'Home' },
    { path: '/about', label: 'About' },
    { path: '/treatments', label: 'Treatments' },
    { path: '/contact', label: 'Contact' }
  ];

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled ? 'bg-white shadow-lg py-2' : 'bg-white/95 backdrop-blur-md py-3'
      }`}
    >
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between">
          {/* Logo */}
          <Link to="/" className="flex items-center space-x-2 sm:space-x-3">
            <img
              src="https://customer-assets.emergentagent.com/job_54b8577b-aded-44ca-bd61-800fb34bb6e2/artifacts/7wv7dh12_image.png"
              alt="Man Clinics Logo"
              className="h-10 sm:h-12 md:h-14 w-auto object-contain"
            />
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center space-x-6 xl:space-x-8">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                className={`text-sm font-medium transition-all duration-200 hover:text-[#D91656] relative group ${
                  location.pathname === link.path ? 'text-[#D91656]' : 'text-gray-700'
                }`}
              >
                {link.label}
                <span className={`absolute -bottom-1 left-0 h-0.5 bg-[#D91656] transition-all duration-300 ${
                  location.pathname === link.path ? 'w-full' : 'w-0 group-hover:w-full'
                }`} />
              </Link>
            ))}
          </nav>

          {/* Desktop CTA Buttons */}
          <div className="hidden lg:flex items-center space-x-4">
            <a
              href={`tel:${clinicData.contact.phone}`}
              className="flex items-center space-x-2 text-[#1E5A8E] hover:text-[#D91656] transition-colors"
            >
              <Phone className="h-4 w-4" />
              <span className="text-sm font-medium">{clinicData.contact.phone}</span>
            </a>
            <Button
              onClick={handleWhatsAppCall}
              className="bg-gradient-to-r from-[#D91656] to-[#B01348] hover:from-[#B01348] hover:to-[#D91656] text-white shadow-md hover:shadow-lg transition-all duration-300"
              size="sm"
            >
              <MessageCircle className="h-4 w-4 mr-2" />
              Book Appointment
            </Button>
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className="lg:hidden p-2 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
            aria-label="Toggle menu"
          >
            {isMobileMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </button>
        </div>

        {/* Mobile Menu */}
        {isMobileMenuOpen && (
          <div className="lg:hidden mt-4 pb-4 border-t border-gray-100 pt-4 animate-in slide-in-from-top-2 duration-200">
            <nav className="flex flex-col space-y-2">
              {navLinks.map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  onClick={() => setIsMobileMenuOpen(false)}
                  className={`px-4 py-3 rounded-lg text-sm font-medium transition-all duration-200 ${
                    location.pathname === link.path
                      ? 'bg-[#D91656]/10 text-[#D91656]'
                      : 'text-gray-700 hover:bg-gray-50'
                  }`}
                >
                  {link.label}
                </Link>
              ))}
            </nav>
            
            <div className="flex flex-col space-y-3 mt-6 pt-6 border-t border-gray-100">
              <a
                href={`tel:${clinicData.contact.phone}`}
                className="flex items-center justify-center space-x-2 px-4 py-3 bg-[#1E5A8E]/10 text-[#1E5A8E] hover:bg-[#1E5A8E]/20 rounded-lg transition-colors"
              >
                <Phone className="h-5 w-5" />
                <span className="font-medium">{clinicData.contact.phone}</span>
              </a>
              <Button
                onClick={handleWhatsAppCall}
                className="bg-gradient-to-r from-[#D91656] to-[#B01348] hover:from-[#B01348] hover:to-[#D91656] text-white shadow-md w-full py-6"
                size="lg"
              >
                <MessageCircle className="h-5 w-5 mr-2" />
                Book Appointment via WhatsApp
              </Button>
            </div>
          </div>
        )}
      </div>
    </header>
  );
};

export default Header;